// src/oura/merchant/myRequests.ts
//
// The merchant's own requests ("Your requests" on Work), in merchant words:
// everything they started, the changes they sent with Ask for a change and
// the calls they asked for (YW, 29 Sep 2026). Pure. Every state and date is
// the server's (routes 18 and 19, BE handoff B9); the one thing read from
// today is the calendar month, because the small change counts per Malaysian
// calendar month (YW, 29 Sep 2026) and a small change is "this month's" only
// while that month lasts.
//
// Two groups (specs v1.1, B3): With us (sent, replied to and not done, a
// small change, a call open or booked) and Done (done changes, closed
// calls). A row says one fact; its tag only what the group head does not:
// a small change, a booked call.

import { malaysianMonthNow, type MyCallRequest, type MyChangeRequest } from "@service/aeo/clientReview";
import type { Tone } from "@oura/ui";
import { docDate, extraSmallChangeIds, smallChangeMonthText } from "@oura/docs";

/** The shared inbox every reply to a request is sent from, when the payload names no address. */
export const OURA_INBOX = "oura@ourteam.ai";

/** The two groups on Work: still with us, and done. */
export type MyRequestGroup = "with_us" | "done";

export const MY_REQUEST_GROUP: Record<MyRequestGroup, { label: string; tone: Tone }> = {
    with_us: { label: "With us", tone: "blue" },
    done: { label: "Done", tone: "green" },
};

/** A call's word where one shows beside its group head: Booked once it is. */
export const MY_CALL_STATE: Record<MyCallRequest["status"], { label: string; tone: Tone }> = {
    open: MY_REQUEST_GROUP.with_us,
    scheduled: { label: "Booked", tone: "green" },
    closed: MY_REQUEST_GROUP.done,
};

/** One thing the business started, a change or a call, in one list. */
export type MyRequestRow =
    | { kind: "change"; id: string; at: string; change: MyChangeRequest }
    | { kind: "call"; id: string; at: string; call: MyCallRequest };

/**
 * Changes and calls together, newest first. A change is ordered by its newest
 * message (`lastAt`, #4930); `at` stays the day it was asked.
 */
export function myRequestRows(requests: readonly MyChangeRequest[], calls: readonly MyCallRequest[]): MyRequestRow[] {
    const time = (iso: string | null | undefined) => Date.parse(iso ?? "") || 0;
    return [
        ...requests.map((change): MyRequestRow => ({ kind: "change", id: change.id, at: change.at, change })),
        ...calls.map((call): MyRequestRow => ({ kind: "call", id: call.id, at: call.at, call })),
    ].sort((a, b) => time(b.kind === "change" ? (b.change.lastAt ?? b.at) : b.at) - time(a.kind === "change" ? (a.change.lastAt ?? a.at) : a.at));
}

/** A change is with us until it is done; a call until it is closed (booked is still with us). */
export function myRequestGroup(row: MyRequestRow): MyRequestGroup {
    if (row.kind === "change") return row.change.status === "done" ? "done" : "with_us";
    return row.call.status === "closed" ? "done" : "with_us";
}

/** How long a done request stays on Home, so the reply that closed it can still be read there. */
export const RECENT_DONE_DAYS = 7;

/**
 * Changes done within the last RECENT_DONE_DAYS, newest first. Done and replied
 * in one step left Home with nothing to show (#4718). A change done with no
 * `doneAt` from the server is not "recent": absent is unknown.
 */
export function recentlyDone(rows: readonly MyRequestRow[], now: Date = new Date()): MyRequestRow[] {
    const since = now.getTime() - RECENT_DONE_DAYS * 24 * 60 * 60 * 1000;
    const doneAt = (r: MyRequestRow): number => (r.kind === "change" && r.change.doneAt ? Date.parse(r.change.doneAt) : NaN);
    return rows
        .filter((r) => r.kind === "change" && myRequestGroup(r) === "done" && doneAt(r) >= since)
        .sort((a, b) => doneAt(b) - doneAt(a));
}

/** How many are with us: the With us group's count. */
export const withUsCount = (requests: readonly MyChangeRequest[], calls: readonly MyCallRequest[] = []): number =>
    requests.filter((r) => r.status !== "done").length + calls.filter((c) => c.status !== "closed").length;

/** The small changes filed beyond the first in their calendar month, by id. */
export function extraSmallChanges(requests: readonly MyChangeRequest[]): ReadonlySet<string> {
    return extraSmallChangeIds(requests);
}

/** The sender, when it is not the viewer: "Mei Ling". Null for the viewer's own. */
export function otherSender(requestedBy: string, viewer: string): string | null {
    const by = requestedBy.trim();
    if (!by) return null;
    const lower = by.toLowerCase();
    return lower === viewer.trim().toLowerCase() || lower === "you" ? null : by;
}

/**
 * A change row's 1 fact: "Angel replied Sun 27 Sep", "Done Mon 28 Sep", or
 * "Sent Tue 29 Sep". Its group head says where it stands.
 */
export function myChangeFact(r: Pick<MyChangeRequest, "at" | "status" | "doneAt" | "reply">): string | null {
    if (r.status === "done") {
        const done = docDate(r.doneAt);
        return done ? `Done ${done}` : "Done";
    }
    if (r.reply) {
        const on = docDate(r.reply.at);
        return `${r.reply.by || "We"} replied${on ? ` ${on}` : ""}`;
    }
    const sent = docDate(r.at);
    return sent ? `Sent ${sent}` : null;
}

/** A call row's 1 fact: "Sent Tue 29 Sep". */
export function myCallFact(c: Pick<MyCallRequest, "at">): string | null {
    const sent = docDate(c.at);
    return sent ? `Sent ${sent}` : null;
}

/**
 * The violet tag a change carries only when it is a small change. One filed
 * in an earlier calendar month names that month ("Small change for August
 * 2026"). `extra`: filed after that month already had its small change,
 * "Extra small change", so 2 in a month never both read as the plan's 1.
 * Null for any other change.
 */
export function smallChangeTag(r: Pick<MyChangeRequest, "status" | "smallChange">, extra = false, month: string = malaysianMonthNow()): { label: string; tone: Tone } | null {
    if (!r.smallChange) return null;
    const named = r.smallChange.month !== month ? smallChangeMonthText(r.smallChange.month) : null;
    if (extra) return { label: named ? `Extra small change for ${named}` : "Extra small change", tone: "violet" };
    if (named) return { label: `Small change for ${named}`, tone: "violet" };
    return { label: r.status === "done" ? "Small change" : "This month's small change", tone: "violet" };
}

/** A notice the bell brought the merchant here with: its own words when it passed them on. */
export interface ReplyNote {
    title: string | null;
    body: string | null;
}

/** The notice the bell passed through navigation state, if any; anything else reads as none. */
export function noticeFromState(state: unknown): ReplyNote {
    const n = (state as { notice?: { title?: unknown; body?: unknown } } | null)?.notice;
    const text = (v: unknown) => (typeof v === "string" && v.trim() ? v.trim() : null);
    return { title: text(n?.title), body: text(n?.body) };
}
